import React, { useState } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  holdings: Holding[];
}

interface UpcomingDividend {
  symbol: string;
  name: string;
  shares: number;
  exDate: string;
  payDate: string;
  yieldPerShare: number;
  currency: string;
  grossAmount: number;
  taxRate: number;
  taxAmount: number;
  netAmount: number;
  status: 'CONFIRMED' | 'PROJECTED';
  reference?: string;
}

export const DividendTracker: React.FC<Props> = ({ holdings }) => {
  const { language } = useLanguage();
  const [activeView, setActiveView] = useState<'calendar' | 'holdings' | 'tax'>('calendar');

  const csgShares = Number(holdings.find(h => h.symbol === 'CSG')?.quantity || 6382);
  const wdcShares = Number(holdings.find(h => h.symbol === 'WDC')?.quantity || 15);
  const muShares = Number(holdings.find(h => h.symbol === 'MU')?.quantity || 8);
  const nvdaShares = Number(holdings.find(h => h.symbol === 'NVDA')?.quantity || 250);

  // Real confirmed corporate action events + projected distributions
  const upcomingDividends: UpcomingDividend[] = [
    {
      symbol: 'WDC',
      name: 'Western Digital Corporation',
      shares: wdcShares,
      exDate: '08.09.2026',
      payDate: '17.09.2026',
      yieldPerShare: 0.15,
      currency: 'USD',
      grossAmount: wdcShares * 0.15,
      taxRate: 30,
      taxAmount: (wdcShares * 0.15) * 0.30,
      netAmount: (wdcShares * 0.15) * 0.70,
      status: 'CONFIRMED',
      reference: 'SLSP Ref: 621229-2124553'
    },
    {
      symbol: 'MU',
      name: 'Micron Technology, Inc.',
      shares: muShares,
      exDate: '05.10.2026',
      payDate: '24.10.2026',
      yieldPerShare: 0.115,
      currency: 'USD',
      grossAmount: muShares * 0.115,
      taxRate: 30,
      taxAmount: (muShares * 0.115) * 0.30,
      netAmount: (muShares * 0.115) * 0.70,
      status: 'PROJECTED'
    },
    {
      symbol: 'NVDA',
      name: 'NVIDIA Corporation',
      shares: nvdaShares,
      exDate: '04.12.2026',
      payDate: '27.12.2026',
      yieldPerShare: 0.04,
      currency: 'USD',
      grossAmount: nvdaShares * 0.04,
      taxRate: 30,
      taxAmount: (nvdaShares * 0.04) * 0.30,
      netAmount: (nvdaShares * 0.04) * 0.70,
      status: 'PROJECTED'
    },
    {
      symbol: 'CSG',
      name: 'Czechoslovak Group',
      shares: csgShares,
      exDate: '15.05.2027',
      payDate: '29.05.2027',
      yieldPerShare: 0.60,
      currency: 'EUR',
      grossAmount: csgShares * 0.60,
      taxRate: 15,
      taxAmount: (csgShares * 0.60) * 0.15,
      netAmount: (csgShares * 0.60) * 0.85,
      status: 'PROJECTED'
    }
  ];

  // Lookup for annual dividend yields
  const getDividendYield = (symbol: string): number => {
    const yields: Record<string, number> = {
      'CSG': 3.20,
      'WDC': 1.25,
      'NVDA': 0.07,
      'MU': 0.45,
      'LITE': 0.00,
      'SNDK': 0.00,
      'SLS': 0.00,
      'AGL': 1.85,
      'SYRE': 0.00,
      'DFTX': 0.00,
      'SKHY': 1.40
    };
    return yields[symbol] !== undefined ? yields[symbol] : 0.00;
  };

  const calculatedDividends = holdings.map(h => {
    const originalPrice = Number(h.current_price || h.avg_cost || 0);
    const value = Number(h.quantity) * originalPrice;
    const divYield = getDividendYield(h.symbol);
    const annualPayout = value * (divYield / 100);
    const yieldOnCost = Number(h.avg_cost) > 0 ? (originalPrice * (divYield / 100) / Number(h.avg_cost)) * 100 : 0;
    const currency = h.currency || (h.symbol === 'CSG' ? 'EUR' : 'USD');

    return {
      symbol: h.symbol,
      name: h.name,
      quantity: Number(h.quantity),
      divYield,
      annualPayout,
      yieldOnCost,
      currency
    };
  });

  const eurUsdRate = 1.09;
  const totalAnnualPayoutEur = calculatedDividends.reduce((sum, item) => {
    const isUsd = item.currency === 'USD';
    const valInEur = isUsd ? item.annualPayout / eurUsdRate : item.annualPayout;
    return sum + valInEur;
  }, 0);

  const monthlyAverageEur = totalAnnualPayoutEur / 12;

  return (
    <div className="bg-white border border-[#1c1917]/20 rounded-2xl p-4 sm:p-6 shadow-sm font-mono text-xs space-y-5">
      {/* Header & View Switcher */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-stone-200/80 pb-4">
        <div className="min-w-0">
          <h3 className="text-xs sm:text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
            <span>📅</span>
            <span>{language === 'sk' ? 'Dividendový Kalendár & Udalosti' : 'Dividend & Corporate Actions'}</span>
          </h3>
          <p className="text-[10px] text-stone-500 mt-0.5 leading-snug">
            {language === 'sk'
              ? 'Projektovaný ročný príjem, zrážkové dane a chronologická časová os potvrdených dividend'
              : 'Projected distributions, withholding tax breakdowns, and chronologic pay-dates'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-1 bg-stone-100 p-1 rounded-xl border border-stone-200 shrink-0">
          <button
            onClick={() => setActiveView('calendar')}
            className={`px-2.5 py-1 rounded-lg font-bold text-[10px] transition-all ${
              activeView === 'calendar' ? 'bg-[#1c1917] text-white shadow-sm' : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            {language === 'sk' ? '📅 Časová Os' : '📅 Timeline'}
          </button>
          <button
            onClick={() => setActiveView('holdings')}
            className={`px-2.5 py-1 rounded-lg font-bold text-[10px] transition-all ${
              activeView === 'holdings' ? 'bg-[#1c1917] text-white shadow-sm' : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            {language === 'sk' ? '📊 Ročný Výnos' : '📊 Annual Yield'}
          </button>
          <button
            onClick={() => setActiveView('tax')}
            className={`px-2.5 py-1 rounded-lg font-bold text-[10px] transition-all ${
              activeView === 'tax' ? 'bg-[#1c1917] text-white shadow-sm' : 'text-stone-600 hover:text-stone-900'
            }`}
          >
            {language === 'sk' ? '⚖️ Dane (WHT)' : '⚖️ Taxes (WHT)'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="bg-stone-50 border border-stone-200/80 rounded-xl p-3.5 space-y-1">
          <span className="text-[9px] uppercase font-bold text-stone-500 block">
            {language === 'sk' ? 'Projektovaný Ročný Príjem' : 'Forecasted Annual Income'}
          </span>
          <span className="text-lg sm:text-xl font-black text-emerald-700 block truncate">
            €{totalAnnualPayoutEur.toLocaleString(language === 'sk' ? 'sk-SK' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="text-[9px] sm:text-[10px] text-stone-500 block truncate">
            {language === 'sk' ? `Z toho CSG ~${(csgShares * 0.60 / 1000).toFixed(1)}k € + US akcie` : `CSG ~${(csgShares * 0.60 / 1000).toFixed(1)}k € + US Stocks`}
          </span>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-xl p-3.5 space-y-1">
          <span className="text-[9px] uppercase font-bold text-stone-500 block">
            {language === 'sk' ? 'Mesačný Priemer (Cash-Flow)' : 'Monthly Average Cash-Flow'}
          </span>
          <span className="text-lg sm:text-xl font-black text-stone-900 block truncate">
            €{monthlyAverageEur.toLocaleString(language === 'sk' ? 'sk-SK' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="text-[9px] sm:text-[10px] text-stone-500 block truncate">
            {language === 'sk' ? 'Priemerný mesačný tok' : 'Average monthly yield flow'}
          </span>
        </div>

        <div className="bg-emerald-50/60 border border-emerald-300/80 rounded-xl p-3.5 space-y-1 sm:col-span-2 lg:col-span-1">
          <span className="text-[9px] uppercase font-bold text-emerald-800 block flex items-center justify-between">
            <span>{language === 'sk' ? 'Najbližšia Dividenda' : 'Next Imminent Payout'}</span>
            <span className="text-[8px] bg-emerald-200 text-emerald-950 font-black px-1.5 py-0.2 rounded">17.09.2026</span>
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg sm:text-xl font-black text-emerald-900">
              +$1.57 USD
            </span>
            <span className="text-[10px] text-emerald-700 font-semibold">({language === 'sk' ? 'Čisté' : 'Net'})</span>
          </div>
          <span className="text-[9px] sm:text-[10px] text-emerald-800 font-bold block truncate">
            Western Digital (WDC) • Ex-date: 08.09.2026
          </span>
        </div>
      </div>

      {/* View 1: Calendar / Timeline */}
      {activeView === 'calendar' && (
        <div className="space-y-3">
          <div className="text-[11px] font-extrabold text-stone-700 uppercase tracking-wider">
            {language === 'sk' ? 'Chronologický Prehľad Výplat' : 'Chronological Distribution Schedule'}
          </div>

          <div className="space-y-2.5">
            {upcomingDividends.map((div, idx) => (
              <div 
                key={idx}
                className={`border rounded-xl p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 transition-all ${
                  div.status === 'CONFIRMED' 
                    ? 'bg-emerald-50/40 border-emerald-300' 
                    : 'bg-stone-50/60 border-stone-200'
                }`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-stone-900">{div.symbol}</span>
                    <span className="text-[10px] text-stone-500">{div.name}</span>
                    <span className={`px-2 py-0.5 text-[8px] font-extrabold rounded-full uppercase ${
                      div.status === 'CONFIRMED' ? 'bg-emerald-200 text-emerald-900' : 'bg-stone-200 text-stone-700'
                    }`}>
                      {div.status === 'CONFIRMED' ? (language === 'sk' ? 'Potvrdené' : 'Confirmed') : (language === 'sk' ? 'Odhad' : 'Projected')}
                    </span>
                  </div>
                  <div className="text-[10px] text-stone-600 flex items-center gap-3">
                    <span><strong>Ex-Date:</strong> {div.exDate}</span>
                    <span>•</span>
                    <span><strong>{language === 'sk' ? 'Výplata:' : 'Pay Date:'}</strong> {div.payDate}</span>
                    <span>•</span>
                    <span><strong>{language === 'sk' ? 'Držané:' : 'Shares:'}</strong> {div.shares} {language === 'sk' ? 'ks' : 'units'}</span>
                  </div>
                  {div.reference && (
                    <div className="text-[9px] text-stone-400 font-mono">
                      {div.reference}
                    </div>
                  )}
                </div>

                <div className="text-right flex md:flex-col items-end justify-between w-full md:w-auto pt-2 md:pt-0 border-t md:border-t-0 border-stone-200">
                  <div className="text-xs font-black text-emerald-700">
                    +{div.netAmount.toFixed(2)} {div.currency} <span className="text-[9px] text-stone-500 font-normal">({language === 'sk' ? 'čisté' : 'net'})</span>
                  </div>
                  <div className="text-[9px] text-stone-500">
                    {language === 'sk' ? 'Hrubý:' : 'Gross:'} {div.grossAmount.toFixed(2)} {div.currency} | {language === 'sk' ? 'Daň' : 'Tax'} ({div.taxRate}%): -{div.taxAmount.toFixed(2)} {div.currency}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View 2: Holdings Yield Breakdown */}
      {activeView === 'holdings' && (
        <div className="space-y-2">
          <div className="text-[11px] font-extrabold text-stone-700 uppercase tracking-wider">
            {language === 'sk' ? 'Ročný Dividendový Potenciál Titulov' : 'Annual Yield Potential by Holding'}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-stone-200 text-stone-400 text-[9px] uppercase">
                  <th className="pb-2">{language === 'sk' ? 'Symbol' : 'Ticker'}</th>
                  <th className="pb-2">{language === 'sk' ? 'Názov' : 'Company'}</th>
                  <th className="pb-2">{language === 'sk' ? 'Kusy' : 'Quantity'}</th>
                  <th className="pb-2">{language === 'sk' ? 'Dividendový Výnos' : 'Dividend Yield'}</th>
                  <th className="pb-2 text-right">{language === 'sk' ? 'Ročný Príjem' : 'Annual Income'}</th>
                  <th className="pb-2 text-right">Yield on Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {calculatedDividends.map(item => (
                  <tr key={item.symbol} className="hover:bg-stone-50 transition-colors">
                    <td className="py-2.5 font-black text-stone-900">{item.symbol}</td>
                    <td className="py-2.5 text-stone-600 truncate max-w-[140px]">{item.name}</td>
                    <td className="py-2.5 font-bold text-stone-700">{item.quantity}</td>
                    <td className="py-2.5 font-bold text-indigo-700">{item.divYield.toFixed(2)}%</td>
                    <td className="py-2.5 text-right font-black text-emerald-700">
                      {item.annualPayout > 0 ? `+${item.annualPayout.toFixed(2)} ${item.currency}` : '—'}
                    </td>
                    <td className="py-2.5 text-right font-bold text-stone-500">
                      {item.yieldOnCost > 0 ? `${item.yieldOnCost.toFixed(2)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View 3: Withholding Tax Rules */}
      {activeView === 'tax' && (
        <div className="space-y-4 text-[11px] text-stone-700 leading-relaxed">
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-2">
            <h4 className="font-extrabold text-stone-900 uppercase tracking-wider text-xs">
              {language === 'sk' 
                ? '🏛️ Daňový Režim Zrážkových Daní (WHT) pre Barsena Limited' 
                : '🏛️ Withholding Tax (WHT) Regime for Barsena Limited'}
            </h4>
            <p>
              {language === 'sk'
                ? 'Pre spoločnosť Barsena Limited (Cyprus / EÚ) platia nasledovné pravidlá zdaňovania dividend:'
                : 'For Barsena Limited (Cyprus / EU tax resident), the following dividend taxation rules apply:'}
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>{language === 'sk' ? 'US Akcie (WDC, NVDA, MU):' : 'US Equities (WDC, NVDA, MU):'}</strong>{' '}
                {language === 'sk'
                  ? 'Štandardná sadzba zrážkovej dane je 30% (odpočítaná priamo v SLSP pri výplate, ako potvrdené v avíze WDC: daň $0.68 zo zisku $2.25).'
                  : 'Standard withholding tax rate is 30% (deducted directly at source by SLSP, confirmed in WDC advice: $0.68 tax on $2.25 gross).'}
              </li>
              <li>
                <strong>{language === 'sk' ? 'EÚ / Holandsko (CSG NV):' : 'EU / Netherlands (CSG NV):'}</strong>{' '}
                {language === 'sk'
                  ? 'Zrážková daň z dividend holandských spoločností je 15% (resp. možnosť uplatnenia smernice EÚ o materských a dcérskych spoločnostiach 0%).'
                  : 'Withholding tax rate for Dutch corporations is 15% (or 0% under the EU Parent-Subsidiary Directive where applicable).'}
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
